import axios from 'axios';
import { pick } from 'lodash';
import { createActionHelpers } from 'vuex-loading';

const { startLoading, endLoading } = createActionHelpers({
    moduleName: 'loading',
});

const types = {
    UPDATE_FUEL_RECEIVING_MOVEMENT: 'UPDATE_FUEL_RECEIVING_MOVEMENT',
    RESET_FUEL_RECEIVING_DETAILS: 'RESET_FUEL_RECEIVING_DETAILS',
    ADD_FUEL_RECEIVING_DETAIL: 'ADD_FUEL_RECEIVING_DETAIL',
    UPDATE_FUEL_RECEIVING_DETAIL: 'UPDATE_FUEL_RECEIVING_DETAIL',
    INITIALIZE_GROUP_DETAILS: 'INITIALIZE_GROUP_DETAILS',
    UPDATE_GROUP_DETAILS: 'UPDATE_GROUP_DETAILS',
    RESET_GROUP_DETAILS: 'RESET_GROUP_DETAILS',
};

const mapToServerObject = movement => {
    let group_details = [];

    group_details = movement.group_details
        .map(g => {
            let tank_details = [];

            for (const receiving of movement.receiving_details) {
                if (receiving.group_no !== g.group_no) {
                    continue;
                }

                // if volume not set, skip
                if (!receiving.receiving_volume) {
                    continue;
                }

                receiving.sales_within = g.sales_within;
                receiving.document_number = movement.document_number;
                const detail = createReceivingDetails(receiving, true);

                tank_details.push(detail);
            }
            // if tank details empty, skip
            if (tank_details.length == 0) {
                return null;
            }

            g.tank_details = tank_details;

            return createGroupDetails(g, true);
        })
        .filter(g => {
            return g !== null;
        });

    return {
        ...pick(movement, [
            'movement_day',
            'document_number',
            'outlet_id',
            'timezone',
        ]),
        group_details,
    };
};

const createReceivingDetails = (input, forServer = false) => {
    let properties = [
        'id',
        'group_no',
        'document_number',
        'tank_id',
        'tank_code',
        'fuel_id',
        'fuel_grade',
        'capacity',
        'start_dipping_id',
        'start_dipping_timestamp',
        'start_dipping_volume',
        'receiving_timestamp',
        'receiving_volume',
        'end_dipping_id',
        'end_dipping_timestamp',
        'end_dipping_volume',
        'sales_within',
        'variance',
        'delivery_variances',
        'calculated_delivery_volume',
        'date_start',
        'date_receiving',
        'date_end',
    ];

    if (forServer) {
        properties = properties.filter(
            p => !['group_no', 'fuel_grade'].includes(p),
        );
    }

    return pick(input, properties);
};

const createGroupDetails = (input, forServer = false) => {
    let properties = [
        'id',
        'tank_details',
        'fuel_id',
        'tank_code',
        'price',
        'sales_within',
        'total_volume',
        'total_cost',
        'group_no',
        'row_index',
        'fuel_grade',
        'variance',
        'tank_group_id',
        'delivery_variances',
        'calculated_delivery_volume',
    ];

    if (forServer) {
        properties = properties.filter(
            p => !['group_no', 'row_index', 'fuel_grade'].includes(p),
        );
    }

    return pick(input, properties);
};

const state = {
    movement: {
        id: null,
        status: null,
        movement_day: null,
        timezone: null,
        outlet_id: null,
        document_number: null,
        group_details: [],
        receiving_details: [],
        deleted_at: null,
    },
};

const getters = {
    movement: state => state.movement,

    movementDay: state => state.movement.movement_day,

    receivingDetails: state => state.movement.receiving_details,

    findReceivingDetailsIndex: (state, getters) => tank_id => {
        return getters.receivingDetails.findIndex(r => r.tank_id === tank_id);
    },

    findReceivingsByGroupNo: (state, getters) => group_no => {
        return getters.receivingDetails.filter(r => r.group_no === group_no);
    },

    findReceivingsByFuelId: (state, getters) => fuel_id => {
        return getters.receivingDetails.filter(r => r.fuel_id === fuel_id);
    },

    groupDetails: state => state.movement.group_details,

    findGroupDetailsIndex: (state, getters) => group_no => {
        return getters.groupDetails.findIndex(g => g.group_no === group_no);
    },

    findGroupByTankGroupId: (state, getters) => tank_group_id => {
        return getters.groupDetails.find(
            g => g.tank_group_id === tank_group_id,
        );
    },

    findGroupByGroupNo: (state, getters) => group_no => {
        return getters.groupDetails.find(g => g.group_no === group_no);
    },
};

const mutations = {
    [types.UPDATE_FUEL_RECEIVING_MOVEMENT](state, movement) {
        state.movement = { ...state.movement, ...movement };
    },

    [types.RESET_FUEL_RECEIVING_DETAILS](state) {
        state.movement.receiving_details = [];
    },

    [types.ADD_FUEL_RECEIVING_DETAIL](state, detail) {
        state.movement.receiving_details.push(detail);
    },

    [types.UPDATE_FUEL_RECEIVING_DETAIL](
        state,
        { receivingDetailIndex, attr, value },
    ) {
        state.movement.receiving_details[receivingDetailIndex][attr] = value;
    },

    [types.INITIALIZE_GROUP_DETAILS](state, groupDetails) {
        state.movement.group_details = groupDetails;
    },

    [types.UPDATE_GROUP_DETAILS](state, { groupDetailIndex, attr, value }) {
        state.movement.group_details[groupDetailIndex][attr] = value;
    },

    [types.RESET_GROUP_DETAILS](state) {
        state.movement.group_details = [];
    },
};

const actions = {
    updateMovement({ commit }, movement) {
        commit(types.UPDATE_FUEL_RECEIVING_MOVEMENT, movement);
    },

    resetReceivingDetails({ commit }) {
        commit(types.RESET_FUEL_RECEIVING_DETAILS);
    },

    addReceivingDetail({ commit, getters, dispatch }, detail) {
        const group = getters.findGroupByTankGroupId(detail.tank_group_id);
        if (!group.fuel_grade) {
            dispatch('updateGroupDetails', {
                group_no: group.group_no,
                attr: 'fuel_grade',
                value: detail.fuel_grade,
            });
        }

        detail.group_no = group.group_no;

        const receivingDetail = createReceivingDetails(detail);

        commit(types.ADD_FUEL_RECEIVING_DETAIL, receivingDetail);
    },

    updateReceivingDetails({ commit, getters }, { tank_id, attr, value }) {
        const receivingDetailIndex = getters.findReceivingDetailsIndex(tank_id);

        commit(types.UPDATE_FUEL_RECEIVING_DETAIL, {
            receivingDetailIndex,
            attr,
            value,
        });
    },

    initializeReceivingGroups({ commit }, tankList) {
        const fuelGroups = [];
        let ctr = 1;

        for (const tank of tankList) {
            if (
                fuelGroups.find(
                    group => group.tank_group_id === tank.tank_group_id,
                )
            ) {
                continue;
            }

            const group = {
                ...tank,
                fuel_grade: tank.fuel_name,
                row_index: null,
                group_no: ctr++,
                total_volume: null,
                total_cost: null,
                sales_within: 0,
                variance: 0,
                tank_group_id: tank.tank_group_id,
                delivery_variances: 0,
                calculated_delivery_volume: 0,
            };

            fuelGroups.push(createGroupDetails(group));
        }

        commit(types.INITIALIZE_GROUP_DETAILS, fuelGroups);
    },

    setExistingReceivingGroups({ commit }, receivings) {
        const fuelGroups = [];
        let ctr = 1;

        for (const receiving of receivings) {
            if (
                fuelGroups.find(
                    group =>
                        group.tank_group_id === receiving.tank.tank_group_id,
                )
            ) {
                continue;
            }

            const group = {
                id: receiving.group_id,
                fuel_id: receiving.fuel_id,
                fuel_grade: receiving.fuel_grade,
                price: receiving.price,
                row_index: null,
                group_no: ctr++,
                total_volume: receiving.group.total_volume,
                total_cost: receiving.group.total_cost,
                sales_within: receiving.group.sales_within,
                variance: receiving.group.variance,
                tank_group_id: receiving.tank.tank_group_id,
                delivery_variances: receiving.group.delivery_variances,
                calculated_delivery_volume:
                    receiving.group.calculated_delivery_volume,
            };

            fuelGroups.push(createGroupDetails(group));
        }

        commit(types.INITIALIZE_GROUP_DETAILS, fuelGroups);
    },

    updateGroupDetails({ commit, getters }, { group_no, attr, value }) {
        const groupDetailIndex = getters.findGroupDetailsIndex(group_no);

        commit(types.UPDATE_GROUP_DETAILS, {
            groupDetailIndex,
            attr,
            value,
        });
    },

    resetGroupDetails({ commit }) {
        commit(types.RESET_GROUP_DETAILS);
    },

    calculateAndSaveTotalVolumeAndTotalCost({ getters, dispatch }, group) {
        const receivings = getters.findReceivingsByGroupNo(group.group_no);

        let totalVolume = 0;
        for (const curr of receivings) {
            totalVolume = totalVolume + numeral(curr.receiving_volume).value();
        }

        dispatch('updateGroupDetails', {
            group_no: group.group_no,
            attr: 'total_volume',
            value: totalVolume,
        });

        const totalCost = totalVolume * numeral(group.price).value();

        dispatch('updateGroupDetails', {
            group_no: group.group_no,
            attr: 'total_cost',
            value: totalCost,
        });
    },

    async createReceivingMovement({ dispatch }) {
        const url = Laroute.route(
            'franchise.inventory.fuel.receiving.create-movement',
        );
        startLoading(dispatch, 'save-receiving');

        try {
            const { data } = await axios.post(
                url,
                mapToServerObject(state.movement),
            );
            endLoading(dispatch, 'save-receiving');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-receiving');
            throw e;
        }
    },

    async updateReceivingMovement({ dispatch, getters }) {
        const movement = getters.movement;
        const url = Laroute.route(
            'franchise.inventory.fuel.receiving.update-movement',
            { id: movement.id },
        );
        startLoading(dispatch, 'save-receiving');

        try {
            const { data } = await axios.patch(
                url,
                mapToServerObject(state.movement),
            );
            endLoading(dispatch, 'save-receiving');

            return data;
        } catch (e) {
            endLoading(dispatch, 'save-receiving');
            throw e;
        }
    },

    async commitReceivingMovement({ dispatch, state }) {
        const url = Laroute.route(
            'franchise.inventory.fuel.receiving.commit-movement',
            { id: state.movement.id },
        );
        startLoading(dispatch, 'commit-receiving');

        try {
            const { data } = await axios.post(url);
            endLoading(dispatch, 'commit-receiving');

            return data;
        } catch (e) {
            endLoading(dispatch, 'commit-receiving');
        }
    },

    async fetchSalesWithin(
        { dispatch, getters, state },
        { since, until, group_no },
    ) {
        startLoading(dispatch, 'get-sales-within');

        const receivings = getters.findReceivingsByGroupNo(group_no);
        const tank_ids = receivings.map(receiving => receiving.tank_id);

        const url = Laroute.route(
            'franchise.inventory.fuel.receiving.sales-within',
        );
        const reportParams = {
            outlet_id: state.movement.outlet_id,
            since,
            until,
            tank_ids,
        };

        try {
            const { data } = await axios.get(url, {
                params: reportParams,
            });

            dispatch('updateGroupDetails', {
                group_no,
                attr: 'sales_within',
                value: data.sales_within,
            });

            for (const receiving of receivings) {
                dispatch('updateReceivingDetails', {
                    tank_id: receiving.tank_id,
                    attr: 'sales_within',
                    value: data.sales_within,
                });
            }

            endLoading(dispatch, 'get-sales-within');

            return data;
        } catch (e) {
            endLoading(dispatch, 'get-sales-within');
        }
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
